(function () {
  const STORAGE_KEY_PREFIX = 'plumelle_spec_table_';
  const COLLAPSED_CLASS = 'meta-row--collapsed';

  let variantSyncBound = false;
  let variantSyncAbortController = null;

  const normalizeUrl = (url) => (url || '').replace(/\/$/, '');

  const getActiveProductUrl = () => {
    const picker = document.querySelector('variant-radios, variant-selects');
    return picker?.dataset?.url || null;
  };

  const initSpecTableCollapsible = () => {
    document.querySelectorAll('.product-metafields-table').forEach((section) => {
      const wrapper = section.querySelector('.meta-table-wrapper');
      if (!wrapper) return;

      const toggle = section.querySelector('.meta-table-toggle');
      if (!toggle) return;

      const sectionId = wrapper.id.replace('meta-table-wrapper-', '');
      const storageKey = `${STORAGE_KEY_PREFIX}${sectionId}_expanded`;

      const rows = wrapper.querySelectorAll('tr[data-collapsible-row]');
      if (!rows.length) return;

      let visibleCount = 5;
      const visibleAttr = section.dataset.visibleRowCount;
      if (visibleAttr) {
        visibleCount = parseInt(visibleAttr, 10) || 5;
      }

      const showMoreLabel = toggle.dataset.showMoreLabel || 'Show more';
      const showLessLabel = toggle.dataset.showLessLabel || 'Show less';

      const icon = toggle.querySelector('.meta-table-toggle__icon');
      const textEl = toggle.querySelector('.meta-table-toggle__text');

      rows.forEach((row) => {
        row.style.removeProperty('display');
        row.style.removeProperty('opacity');
        row.style.removeProperty('transition');
      });

      const updateToggleUi = (isExpanded) => {
        if (isExpanded) {
          toggle.classList.add('active');
          toggle.setAttribute('aria-expanded', 'true');
          if (icon) {
            icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>';
          }
          if (textEl) textEl.textContent = showLessLabel;
        } else {
          toggle.classList.remove('active');
          toggle.setAttribute('aria-expanded', 'false');
          if (icon) {
            icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>';
          }
          if (textEl) textEl.textContent = showMoreLabel;
        }
      };

      const syncGroupHeadings = () => {
        wrapper.querySelectorAll('.meta-group-heading-row').forEach((heading) => {
          let sibling = heading.nextElementSibling;
          let anyVisible = false;

          while (sibling && !sibling.classList.contains('meta-group-heading-row')) {
            if (
              sibling.matches('tr[data-collapsible-row]') &&
              !sibling.classList.contains(COLLAPSED_CLASS)
            ) {
              anyVisible = true;
              break;
            }
            sibling = sibling.nextElementSibling;
          }

          heading.classList.toggle('meta-group-heading-row--hidden', !anyVisible);
          heading.setAttribute('aria-hidden', anyVisible ? 'false' : 'true');
        });
      };

      const applyCollapsedState = (isExpanded) => {
        const collapsedRows = Array.from(rows).slice(visibleCount);

        collapsedRows.forEach((row) => {
          if (isExpanded) {
            row.classList.remove(COLLAPSED_CLASS);
            row.setAttribute('aria-hidden', 'false');
          } else {
            row.classList.add(COLLAPSED_CLASS);
            row.setAttribute('aria-hidden', 'true');
          }
        });

        syncGroupHeadings();
        updateToggleUi(isExpanded);
      };

      let isExpanded = false;
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored === 'true') {
          isExpanded = true;
        }
      } catch (e) {
        // localStorage not available or blocked
      }

      applyCollapsedState(isExpanded);

      if (toggle.dataset.collapsibleBound === 'true') return;
      toggle.dataset.collapsibleBound = 'true';

      toggle.addEventListener('click', () => {
        isExpanded = !isExpanded;
        applyCollapsedState(isExpanded);

        try {
          localStorage.setItem(storageKey, isExpanded ? 'true' : 'false');
        } catch (e) {
          // ignore storage errors
        }
      });

      toggle.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle.click();
        }
      });
    });
  };

  const updateSpecTableForVariant = async (variantId, productUrl) => {
    const specSections = document.querySelectorAll(
      '.product-metafields-table[data-product-url][data-section-id]'
    );

    const normalizedProductUrl = normalizeUrl(productUrl);

    for (const section of specSections) {
      if (normalizeUrl(section.dataset.productUrl) !== normalizedProductUrl) continue;

      const wrapper = section.querySelector('.meta-table-wrapper');
      const sectionId = section.dataset.sectionId;
      if (!wrapper || !sectionId || !variantId) continue;

      variantSyncAbortController?.abort();
      variantSyncAbortController = new AbortController();

      // Ask for this section id. A full product URL without ?view= returns the
      // default template, whose section id does not match plumelle-mobile-atc-b,
      // so the table never swaps. ?variant= selects that size's metafields.
      const requestUrl = `${productUrl}?section_id=${encodeURIComponent(sectionId)}&variant=${encodeURIComponent(variantId)}`;

      try {
        const response = await fetch(requestUrl, {
          signal: variantSyncAbortController.signal,
        });
        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newWrapper = html.querySelector(
          `#shopify-section-${sectionId} .meta-table-wrapper`
        );

        if (newWrapper) {
          wrapper.innerHTML = newWrapper.innerHTML;
          initSpecTableCollapsible();
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Product spec table variant sync failed:', error);
        }
      }
    }
  };

  const initSpecTableVariantSync = () => {
    const specSections = document.querySelectorAll(
      '.product-metafields-table[data-product-url][data-section-id]'
    );
    if (!specSections.length || variantSyncBound) return;

    variantSyncBound = true;

    document.addEventListener('variant:change', (event) => {
      const variant = event.detail?.variant;
      if (!variant?.id) return;

      const productUrl = getActiveProductUrl();
      if (!productUrl) return;

      updateSpecTableForVariant(variant.id, productUrl);
    });
  };

  const init = () => {
    initSpecTableCollapsible();
    initSpecTableVariantSync();
  };

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('DOMContentLoaded', init);
})();